import {RootState} from '../../../redux/store';
import {AssetFolderEntityType} from '../Assets.enums';
import {AssetDisplayState, Folder} from '../Assets.types';
import {ActionCreatorWithPayload} from '@reduxjs/toolkit';
import {ActionCreatorWithoutPayload} from '@reduxjs/toolkit';

/**
 * Configuration object used by useAssetDisplay hook
 * Contains selectors, actions, and entity-specific settings
 */
export interface UseAssetDisplayConfig {
  // Selectors
  selectAssetDisplayState: (state: RootState) => AssetDisplayState;
  selectAssetTree: (state: RootState) => Folder | null;
  selectCurrentFolderId: (state: RootState) => number | null;
  selectEntityId: ((state: RootState) => number | undefined) | undefined;

  // Tree actions
  setAssetTree: ActionCreatorWithPayload<Folder | null>;
  setIsLoadingTree: ActionCreatorWithPayload<boolean>;

  // Navigation actions
  setCurrentFolderId: ActionCreatorWithPayload<number | null>;

  // Selection actions
  setSelectedAssetIds: ActionCreatorWithPayload<number[]>;
  setSelectedFolderIds: ActionCreatorWithPayload<number[]>;
  clearSelection: ActionCreatorWithoutPayload;

  // Loading actions
  setIsLoadingFolderAssets: ActionCreatorWithPayload<boolean>;

  // Search actions
  setSearchTerm: ActionCreatorWithPayload<string>;
  setSearchType: ActionCreatorWithPayload<'name' | 'content'>;
  setSearchResults: ActionCreatorWithPayload<Folder | null>;
  setIsSearching: ActionCreatorWithPayload<boolean>;
  clearSearch: ActionCreatorWithoutPayload;

  // CRUD actions
  updateAssetName: ActionCreatorWithPayload<{assetId: number; newName: string}>;
  updateFolderName: ActionCreatorWithPayload<{folderId: number; newName: string}>;
  removeAsset: ActionCreatorWithPayload<number>;
  removeFolder: ActionCreatorWithPayload<number>;

  // Reset action
  resetState: ActionCreatorWithoutPayload;

  // Entity configuration
  entityType: AssetFolderEntityType;
  loadAssetTreeDepth: number;
  supportsLinking?: boolean;
}

// Brand Assets imports
import {
  setSelectedAssetIds as setBrandSelectedAssetIds,
  setSelectedFolderIds as setBrandSelectedFolderIds,
  setCurrentFolderId as setBrandCurrentFolderId,
  setAssetTree as setBrandAssetTree,
  setIsLoadingFolderAssets as setBrandIsLoadingFolderAssets,
  setIsLoadingTree as setBrandIsLoadingTree,
  clearSelection as clearBrandSelection,
  clearSearch as clearBrandSearch,
  clearBrandAssets,
  updateAssetName as updateBrandAssetName,
  updateFolderName as updateBrandFolderName,
  removeAsset as removeBrandAsset,
  removeFolder as removeBrandFolder,
  setSearchTerm as setBrandSearchTerm,
  setSearchType as setBrandSearchType,
  setSearchResults as setBrandSearchResults,
  setIsSearching as setBrandIsSearching,
} from '../../brandGuidelines/store/brandAssets/brandAssets.slice';

// Assets Library imports
import {
  setSelectedAssetIds as setLibrarySelectedAssetIds,
  setSelectedFolderIds as setLibrarySelectedFolderIds,
  setCurrentFolderId as setLibraryCurrentFolderId,
  setAssetTree as setLibraryAssetTree,
  setIsLoadingFolderAssets as setLibraryIsLoadingFolderAssets,
  setIsLoadingTree as setLibraryIsLoadingTree,
  clearSelection as clearLibrarySelection,
  clearSearch as clearLibrarySearch,
  clearAssetsLibrary,
  updateAssetName as updateLibraryAssetName,
  updateFolderName as updateLibraryFolderName,
  removeAsset as removeLibraryAsset,
  removeFolder as removeLibraryFolder,
  setSearchTerm as setLibrarySearchTerm,
  setSearchType as setLibrarySearchType,
  setSearchResults as setLibrarySearchResults,
  setIsSearching as setLibraryIsSearching,
} from './assetsLibrary/assetsLibrary.slice';

// Template Assets imports
import {
  setSelectedAssetIds as setTemplateSelectedAssetIds,
  setSelectedFolderIds as setTemplateSelectedFolderIds,
  setCurrentFolderId as setTemplateCurrentFolderId,
  setAssetTree as setTemplateAssetTree,
  setIsLoadingFolderAssets as setTemplateIsLoadingFolderAssets,
  setIsLoadingTree as setTemplateIsLoadingTree,
  clearSelection as clearTemplateSelection,
  clearSearch as clearTemplateSearch,
  clearTemplateAssets,
  updateAssetName as updateTemplateAssetName,
  updateFolderName as updateTemplateFolderName,
  removeAsset as removeTemplateAsset,
  removeFolder as removeTemplateFolder,
  setSearchTerm as setTemplateSearchTerm,
  setSearchType as setTemplateSearchType,
  setSearchResults as setTemplateSearchResults,
  setIsSearching as setTemplateIsSearching,
} from '../../creativeTemplates/store/creativeTemplatePage/templateAssets/templateAssets.slice';

// Creative Assets imports
import {
  setSelectedAssetIds as setCreativeSelectedAssetIds,
  setSelectedFolderIds as setCreativeSelectedFolderIds,
  setCurrentFolderId as setCreativeCurrentFolderId,
  setAssetTree as setCreativeAssetTree,
  setIsLoadingFolderAssets as setCreativeIsLoadingFolderAssets,
  setIsLoadingTree as setCreativeIsLoadingTree,
  clearSelection as clearCreativeSelection,
  clearSearch as clearCreativeSearch,
  clearCreativeAssets,
  updateAssetName as updateCreativeAssetName,
  updateFolderName as updateCreativeFolderName,
  removeAsset as removeCreativeAsset,
  removeFolder as removeCreativeFolder,
  setSearchTerm as setCreativeSearchTerm,
  setSearchType as setCreativeSearchType,
  setSearchResults as setCreativeSearchResults,
  setIsSearching as setCreativeIsSearching,
} from '../../creatives/store/creativePage/creativeAssets/creativeAssets.slice';

// AI Content Project Assets imports
import {
  setProjectAssetsSelectedAssetIds,
  setProjectAssetsSelectedFolderIds,
  setProjectAssetsCurrentFolderId,
  setProjectAssetTree,
  setProjectAssetsIsLoadingFolderAssets,
  setProjectAssetsIsLoadingTree,
  clearProjectAssetsSelection,
  clearProjectAssetsSearch,
  clearProjectAssets,
  updateProjectAssetsAssetName,
  updateProjectAssetsFolderName,
  removeProjectAssetsAsset,
  removeProjectAssetsFolder,
  setProjectAssetsSearchTerm,
  setProjectAssetsSearchType,
  setProjectAssetsSearchResults,
  setProjectAssetsIsSearching,
} from '../../aiContent/store/aiContentProject/aiContentProject.slice';

// Widget Assets imports
import {
  setSelectedAssetIds as setWidgetSelectedAssetIds,
  setSelectedFolderIds as setWidgetSelectedFolderIds,
  setCurrentFolderId as setWidgetCurrentFolderId,
  setAssetTree as setWidgetAssetTree,
  setIsLoadingFolderAssets as setWidgetIsLoadingFolderAssets,
  setIsLoadingTree as setWidgetIsLoadingTree,
  clearSelection as clearWidgetSelection,
  clearSearch as clearWidgetSearch,
  clearWidgetAssets,
  updateAssetName as updateWidgetAssetName,
  updateFolderName as updateWidgetFolderName,
  removeAsset as removeWidgetAsset,
  removeFolder as removeWidgetFolder,
  setSearchTerm as setWidgetSearchTerm,
  setSearchType as setWidgetSearchType,
  setSearchResults as setWidgetSearchResults,
  setIsSearching as setWidgetIsSearching,
} from '../../creativeTemplates/store/creativeWidgetPage/widgetAssets/widgetAssets.slice';

// Template Dynamic Parameters Assets imports
import {
  setSelectedAssetIds as setDpSelectedAssetIds,
  setSelectedFolderIds as setDpSelectedFolderIds,
  setCurrentFolderId as setDpCurrentFolderId,
  setAssetTree as setDpAssetTree,
  setIsLoadingFolderAssets as setDpIsLoadingFolderAssets,
  setIsLoadingTree as setDpIsLoadingTree,
  clearSelection as clearDpSelection,
  clearSearch as clearDpSearch,
  clearDpAssets,
  updateAssetName as updateDpAssetName,
  updateFolderName as updateDpFolderName,
  removeAsset as removeDpAsset,
  removeFolder as removeDpFolder,
  setSearchTerm as setDpSearchTerm,
  setSearchType as setDpSearchType,
  setSearchResults as setDpSearchResults,
  setIsSearching as setDpIsSearching,
} from '../../creativeTemplates/store/creativeTemplatePage/dynamicParams/dpAssets/dpAssets.slice';

/**
 * Centralized configuration object for asset display across all entity types
 * Maps AssetFolderEntityType to UseAssetDisplayConfig containing selectors, actions, and settings
 */
export const assetDisplayConfigs: Record<AssetFolderEntityType, UseAssetDisplayConfig> = {
  [AssetFolderEntityType.Brand]: {
    // Selectors
    selectAssetDisplayState: (state: RootState) => state.brandGuidelinesPage.brandAssets,
    selectAssetTree: (state: RootState) => state.brandGuidelinesPage.brandAssets.assetTree,
    selectCurrentFolderId: (state: RootState) =>
      state.brandGuidelinesPage.brandAssets.currentFolderId,
    selectEntityId: undefined,

    // Actions
    setAssetTree: setBrandAssetTree,
    setCurrentFolderId: setBrandCurrentFolderId,
    setIsLoadingFolderAssets: setBrandIsLoadingFolderAssets,
    setIsLoadingTree: setBrandIsLoadingTree,
    setSelectedAssetIds: setBrandSelectedAssetIds,
    setSelectedFolderIds: setBrandSelectedFolderIds,
    clearSelection: clearBrandSelection,
    clearSearch: clearBrandSearch,
    resetState: clearBrandAssets,
    updateAssetName: updateBrandAssetName,
    updateFolderName: updateBrandFolderName,
    removeAsset: removeBrandAsset,
    removeFolder: removeBrandFolder,
    setSearchTerm: setBrandSearchTerm,
    setSearchType: setBrandSearchType,
    setSearchResults: setBrandSearchResults,
    setIsSearching: setBrandIsSearching,

    // Entity config
    entityType: AssetFolderEntityType.Brand,
    loadAssetTreeDepth: 99,
    supportsLinking: true,
  },

  [AssetFolderEntityType.AssetsLibrary]: {
    // Selectors
    selectAssetDisplayState: (state: RootState) => state.assetsLibraryPage.assetsLibrary,
    selectAssetTree: (state: RootState) => state.assetsLibraryPage.assetsLibrary.assetTree,
    selectCurrentFolderId: (state: RootState) =>
      state.assetsLibraryPage.assetsLibrary.currentFolderId,
    selectEntityId: undefined,

    // Actions
    setAssetTree: setLibraryAssetTree,
    setCurrentFolderId: setLibraryCurrentFolderId,
    setIsLoadingFolderAssets: setLibraryIsLoadingFolderAssets,
    setIsLoadingTree: setLibraryIsLoadingTree,
    setSelectedAssetIds: setLibrarySelectedAssetIds,
    setSelectedFolderIds: setLibrarySelectedFolderIds,
    clearSelection: clearLibrarySelection,
    clearSearch: clearLibrarySearch,
    resetState: clearAssetsLibrary,
    updateAssetName: updateLibraryAssetName,
    updateFolderName: updateLibraryFolderName,
    removeAsset: removeLibraryAsset,
    removeFolder: removeLibraryFolder,
    setSearchTerm: setLibrarySearchTerm,
    setSearchType: setLibrarySearchType,
    setSearchResults: setLibrarySearchResults,
    setIsSearching: setLibraryIsSearching,

    // Entity config
    entityType: AssetFolderEntityType.AssetsLibrary,
    loadAssetTreeDepth: 1,
    supportsLinking: false,
  },

  [AssetFolderEntityType.CreativeTemplate]: {
    // Selectors
    selectAssetDisplayState: (state: RootState) => state.creativeTemplatePage.templateAssets,
    selectAssetTree: (state: RootState) => state.creativeTemplatePage.templateAssets.assetTree,
    selectCurrentFolderId: (state: RootState) =>
      state.creativeTemplatePage.templateAssets.currentFolderId,
    selectEntityId: (state: RootState) => state.creativeTemplatePage.template?.id,

    // Actions
    setAssetTree: setTemplateAssetTree,
    setCurrentFolderId: setTemplateCurrentFolderId,
    setIsLoadingFolderAssets: setTemplateIsLoadingFolderAssets,
    setIsLoadingTree: setTemplateIsLoadingTree,
    setSelectedAssetIds: setTemplateSelectedAssetIds,
    setSelectedFolderIds: setTemplateSelectedFolderIds,
    clearSelection: clearTemplateSelection,
    clearSearch: clearTemplateSearch,
    resetState: clearTemplateAssets,
    updateAssetName: updateTemplateAssetName,
    updateFolderName: updateTemplateFolderName,
    removeAsset: removeTemplateAsset,
    removeFolder: removeTemplateFolder,
    setSearchTerm: setTemplateSearchTerm,
    setSearchType: setTemplateSearchType,
    setSearchResults: setTemplateSearchResults,
    setIsSearching: setTemplateIsSearching,

    // Entity config
    entityType: AssetFolderEntityType.CreativeTemplate,
    loadAssetTreeDepth: 99,
    supportsLinking: true,
  },

  [AssetFolderEntityType.TemplateDynamicParameters]: {
    // Selectors
    selectAssetDisplayState: (state: RootState) =>
      state.creativeTemplatePage.dynamicParams.dpAssets,
    selectAssetTree: (state: RootState) =>
      state.creativeTemplatePage.dynamicParams.dpAssets.assetTree,
    selectCurrentFolderId: (state: RootState) =>
      state.creativeTemplatePage.dynamicParams.dpAssets.currentFolderId,
    selectEntityId: (state: RootState) => state.creativeTemplatePage.template?.id,

    // Actions
    setAssetTree: setDpAssetTree,
    setCurrentFolderId: setDpCurrentFolderId,
    setIsLoadingFolderAssets: setDpIsLoadingFolderAssets,
    setIsLoadingTree: setDpIsLoadingTree,
    setSelectedAssetIds: setDpSelectedAssetIds,
    setSelectedFolderIds: setDpSelectedFolderIds,
    clearSelection: clearDpSelection,
    clearSearch: clearDpSearch,
    resetState: clearDpAssets,
    updateAssetName: updateDpAssetName,
    updateFolderName: updateDpFolderName,
    removeAsset: removeDpAsset,
    removeFolder: removeDpFolder,
    setSearchTerm: setDpSearchTerm,
    setSearchType: setDpSearchType,
    setSearchResults: setDpSearchResults,
    setIsSearching: setDpIsSearching,

    // Entity config
    entityType: AssetFolderEntityType.TemplateDynamicParameters,
    loadAssetTreeDepth: 99,
    supportsLinking: true,
  },

  [AssetFolderEntityType.Creative]: {
    // Selectors
    selectAssetDisplayState: (state: RootState) => state.creativePage.creativeAssets,
    selectAssetTree: (state: RootState) => state.creativePage.creativeAssets.assetTree,
    selectCurrentFolderId: (state: RootState) => state.creativePage.creativeAssets.currentFolderId,
    selectEntityId: (state: RootState) => state.creativePage.creative?.id,

    // Actions
    setAssetTree: setCreativeAssetTree,
    setCurrentFolderId: setCreativeCurrentFolderId,
    setIsLoadingFolderAssets: setCreativeIsLoadingFolderAssets,
    setIsLoadingTree: setCreativeIsLoadingTree,
    setSelectedAssetIds: setCreativeSelectedAssetIds,
    setSelectedFolderIds: setCreativeSelectedFolderIds,
    clearSelection: clearCreativeSelection,
    clearSearch: clearCreativeSearch,
    resetState: clearCreativeAssets,
    updateAssetName: updateCreativeAssetName,
    updateFolderName: updateCreativeFolderName,
    removeAsset: removeCreativeAsset,
    removeFolder: removeCreativeFolder,
    setSearchTerm: setCreativeSearchTerm,
    setSearchType: setCreativeSearchType,
    setSearchResults: setCreativeSearchResults,
    setIsSearching: setCreativeIsSearching,

    // Entity config
    entityType: AssetFolderEntityType.Creative,
    loadAssetTreeDepth: 1,
    supportsLinking: true,
  },

  [AssetFolderEntityType.AiContentProject]: {
    // Selectors
    selectAssetDisplayState: (state: RootState) => state.aiContentProjectPage.projectAssets,
    selectAssetTree: (state: RootState) => state.aiContentProjectPage.projectAssets.assetTree,
    selectCurrentFolderId: (state: RootState) =>
      state.aiContentProjectPage.projectAssets.currentFolderId,
    selectEntityId: (state: RootState) => state.aiContentProjectPage.project?.id,

    // Actions
    setAssetTree: setProjectAssetTree,
    setCurrentFolderId: setProjectAssetsCurrentFolderId,
    setIsLoadingFolderAssets: setProjectAssetsIsLoadingFolderAssets,
    setIsLoadingTree: setProjectAssetsIsLoadingTree,
    setSelectedAssetIds: setProjectAssetsSelectedAssetIds,
    setSelectedFolderIds: setProjectAssetsSelectedFolderIds,
    clearSelection: clearProjectAssetsSelection,
    clearSearch: clearProjectAssetsSearch,
    resetState: clearProjectAssets,
    updateAssetName: updateProjectAssetsAssetName,
    updateFolderName: updateProjectAssetsFolderName,
    removeAsset: removeProjectAssetsAsset,
    removeFolder: removeProjectAssetsFolder,
    setSearchTerm: setProjectAssetsSearchTerm,
    setSearchType: setProjectAssetsSearchType,
    setSearchResults: setProjectAssetsSearchResults,
    setIsSearching: setProjectAssetsIsSearching,

    // Entity config
    entityType: AssetFolderEntityType.AiContentProject,
    loadAssetTreeDepth: 1,
    supportsLinking: true,
  },

  [AssetFolderEntityType.Widget]: {
    // Selectors
    selectAssetDisplayState: (state: RootState) => state.creativeWidgetPage.widgetAssets,
    selectAssetTree: (state: RootState) => state.creativeWidgetPage.widgetAssets.assetTree,
    selectCurrentFolderId: (state: RootState) =>
      state.creativeWidgetPage.widgetAssets.currentFolderId,
    selectEntityId: (state: RootState) => state.creativeWidgetPage.widget?.id,

    // Actions
    setAssetTree: setWidgetAssetTree,
    setCurrentFolderId: setWidgetCurrentFolderId,
    setIsLoadingFolderAssets: setWidgetIsLoadingFolderAssets,
    setIsLoadingTree: setWidgetIsLoadingTree,
    setSelectedAssetIds: setWidgetSelectedAssetIds,
    setSelectedFolderIds: setWidgetSelectedFolderIds,
    clearSelection: clearWidgetSelection,
    clearSearch: clearWidgetSearch,
    resetState: clearWidgetAssets,
    updateAssetName: updateWidgetAssetName,
    updateFolderName: updateWidgetFolderName,
    removeAsset: removeWidgetAsset,
    removeFolder: removeWidgetFolder,
    setSearchTerm: setWidgetSearchTerm,
    setSearchType: setWidgetSearchType,
    setSearchResults: setWidgetSearchResults,
    setIsSearching: setWidgetIsSearching,

    // Entity config
    entityType: AssetFolderEntityType.Widget,
    loadAssetTreeDepth: 99,
    supportsLinking: true,
  },
};
